import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Loader2, MapPin, Phone, Mail, Building2, Save, Warehouse, Truck, Package, Plus, Trash2, DollarSign, Navigation } from 'lucide-react';

const DROPOFF_HUBS = [
  { name: 'OGBA Hub', address: 'Shop i012, Ogba Shopping Mall, Abiodun Jagun Street, Off Wempco Road, Beside Sunday Market, Ogba, Lagos', phone: '' },
  { name: 'IKOYI Hub', address: 'Shop D84, Dolphin Plaza, Corporation Drive, Dolphin Estate, Ikoyi, Lagos', phone: '' },
  { name: 'IPAJA Hub', address: 'C6 Suite7, Ground Floor, Solomon Adeola Lane, Rauf Aregbesola Shopping Mall, Pako Bustop, Ipaja, Lagos', phone: '' },
  { name: 'Ojota Hub', address: 'Shop 18, Winter Plaza, 57 Ogudu Road, Ojota, Lagos', phone: '' },
  { name: 'Ikota Hub', address: 'Shop E110, Road 2, Ikota Shopping Complex, Lagos', phone: '' },
  { name: 'Osapa London Hub', address: 'Q-Mall, Lekki Beach Rd, Jakande Roundabout, Lekki, Lagos', phone: '' },
  { name: 'Fez Abuja Hub', address: 'Suite 64, De Avalon Plaza, Ajose Ade Ogun Crescent, Utako, Abuja', phone: '+234 903 598 6582' },
  { name: 'Fez Asaba Hub', address: 'No 19 Jossy Plaza, Opp Ezebuilo Filling Station, After Osadenis Junction, Asaba, Delta State', phone: '+234 902 561 2043' },
  { name: 'Fez Ibadan Hub', address: 'Shop F1, Tejumade Square, Ago Tapa, Mokola, Ibadan', phone: '+234 702 547 8617' },
  { name: 'Fez Benin Hub', address: '16b Avielele Close, Off Ojomoh Street, Off Etete Road, GRA, Benin', phone: '+234 708 271 3300' },
  { name: 'Fez Enugu Hub', address: 'Shop C1, Coal City Mega Pavilion Plaza (Tecno Plaza), Market Road, By Egbuna Junction, Ogui Road, Enugu', phone: '+234 802 283 2704' },
  { name: 'Fez Ogun Hub', address: 'Shop 69, Omida Shopping Mall, Omida, Abeokuta, Ogun State', phone: '+234 906 418 8120' },
  { name: 'Fez Warri Hub', address: 'Naomi Shopping Plaza, 7 Airport Road, Effurun, Warri, Delta', phone: '' },
  { name: 'Fez Port Harcourt Hub', address: 'Roxy Plaza, Opposite Polaris Bank, Rumuodara, Port Harcourt', phone: '+234 703 760 7631' },
  { name: 'Fez Akure Hub', address: 'Cuda Complex, Opp GTBank Alagbaka, Akure', phone: '+234 706 579 7706' },
  { name: 'Fez Osun Hub', address: 'Jato Odedosu Shopping Complex, Ayetoro Road, Owode, Osogbo, Osun State', phone: '' },
  { name: 'Fez Uyo Hub', address: 'Belderick House, 15 Mbaba Afia Street, Off Aka Road (Near IBB/Aka Road Junction), Uyo', phone: '+234 813 632 6573' },
];

const nigerianStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
  'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

interface PickupLocation {
  name: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
}

interface LocationFee {
  state: string;
  fee: number;
}

export function PickupLocationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deliveryPreference, setDeliveryPreference] = useState<'self' | 'partner'>('partner');
  const [selfDeliveryFeeType, setSelfDeliveryFeeType] = useState<'fixed' | 'per_location'>('fixed');
  const [selfDeliveryFixedFee, setSelfDeliveryFixedFee] = useState<number>(0);
  const [selfDeliveryLocationFees, setSelfDeliveryLocationFees] = useState<LocationFee[]>([]);
  const [location, setLocation] = useState<PickupLocation>({
    name: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: ''
  });
  const [hasExistingLocation, setHasExistingLocation] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchPickupLocation();
    }
  }, [user?.id]);

  const fetchPickupLocation = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('pickup_address_name, pickup_address_phone, pickup_address_email, pickup_address_street, pickup_address_city, pickup_address_state, business_name, phone, delivery_preference, self_delivery_fee_type, self_delivery_fixed_fee, self_delivery_location_fees')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      if (data) {
        const hasLocation = !!(data.pickup_address_street);
        setHasExistingLocation(hasLocation);
        setDeliveryPreference((data.delivery_preference as 'self' | 'partner') || 'partner');
        setSelfDeliveryFeeType((data.self_delivery_fee_type as 'fixed' | 'per_location') || 'fixed');
        setSelfDeliveryFixedFee(Number(data.self_delivery_fixed_fee) || 0);
        setSelfDeliveryLocationFees(Array.isArray(data.self_delivery_location_fees) ? (data.self_delivery_location_fees as unknown as LocationFee[]) : []);
        setLocation({
          name: data.pickup_address_name || data.business_name || '',
          phone: data.pickup_address_phone || data.phone || '',
          email: data.pickup_address_email || '',
          street: data.pickup_address_street || '',
          city: data.pickup_address_city || '',
          state: data.pickup_address_state || ''
        });
      }
    } catch (error) {
      console.error('Error fetching pickup location:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Only validate pickup address if using partner delivery
    if (deliveryPreference === 'partner') {
      if (!location.street || !location.city || !location.state) {
        toast.error('Please fill in all required address fields');
        return;
      }

      if (!location.phone) {
        toast.error('Please provide a contact phone number');
        return;
      }
    }

    setSaving(true);
    try {
      const updateData: Record<string, unknown> = {
        delivery_preference: deliveryPreference,
        updated_at: new Date().toISOString()
      };

      // Only update pickup address if using partner delivery
      if (deliveryPreference === 'partner') {
        updateData.pickup_address_name = location.name;
        updateData.pickup_address_phone = location.phone;
        updateData.pickup_address_email = location.email;
        updateData.pickup_address_street = location.street;
        updateData.pickup_address_city = location.city;
        updateData.pickup_address_state = location.state;
      } else {
        // Save self-delivery fee settings
        updateData.self_delivery_fee_type = selfDeliveryFeeType;
        updateData.self_delivery_fixed_fee = selfDeliveryFixedFee;
        updateData.self_delivery_location_fees = selfDeliveryLocationFees;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user?.id);

      if (error) throw error;

      if (deliveryPreference === 'partner') {
        setHasExistingLocation(true);
      }
      toast.success('Delivery settings saved successfully!');
    } catch (error) {
      console.error('Error saving delivery settings:', error);
      toast.error('Failed to save delivery settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof PickupLocation, value: string) => {
    setLocation(prev => ({ ...prev, [field]: value }));
  };

  const addLocationFee = () => {
    setSelfDeliveryLocationFees(prev => [...prev, { state: '', fee: 0 }]);
  };

  const updateLocationFee = (index: number, field: 'state' | 'fee', value: string | number) => {
    setSelfDeliveryLocationFees(prev => 
      prev.map((item, i) => i === index ? { ...item, [field]: value } : item)
    );
  };

  const removeLocationFee = (index: number) => {
    setSelfDeliveryLocationFees(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delivery Preference Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Delivery Method
          </CardTitle>
          <CardDescription>
            Choose how you want to handle order deliveries
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={deliveryPreference}
            onValueChange={(value) => setDeliveryPreference(value as 'self' | 'partner')}
            className="grid gap-4"
          >
            <div className={`flex items-start space-x-4 p-4 rounded-lg border-2 transition-colors ${
              deliveryPreference === 'partner' ? 'border-primary bg-primary/5' : 'border-border'
            }`}>
              <RadioGroupItem value="partner" id="partner" className="mt-1" />
              <Label htmlFor="partner" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-semibold">
                  <Package className="h-4 w-4 text-primary" />
                  Use Our Delivery Partner (Sendbox)
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  We handle pickup and delivery through our logistics partner. You'll need to set up a pickup address.
                  Enjoy tracking, insurance, and reliable nationwide coverage.
                </p>
              </Label>
            </div>
            
            <div className={`flex items-start space-x-4 p-4 rounded-lg border-2 transition-colors ${
              deliveryPreference === 'self' ? 'border-primary bg-primary/5' : 'border-border'
            }`}>
              <RadioGroupItem value="self" id="self" className="mt-1" />
              <Label htmlFor="self" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-semibold">
                  <Truck className="h-4 w-4 text-orange-500" />
                  Self Delivery
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  You handle all deliveries yourself. You'll coordinate directly with buyers for delivery arrangements.
                  Ideal for local sellers or those with existing logistics.
                </p>
              </Label>
            </div>
          </RadioGroup>

          {deliveryPreference === 'self' && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>Self Delivery Selected:</strong> You are responsible for delivering orders to customers. 
                Customers will see "Seller handles delivery" at checkout.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pickup Location - Only shown when partner delivery is selected */}
      {deliveryPreference === 'partner' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warehouse className="h-5 w-5" />
              Pickup Location / Warehouse Address
            </CardTitle>
            <CardDescription>
              Set your warehouse or pickup address for Sendbox deliveries. This is where couriers will pick up orders for shipping.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasExistingLocation && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> You haven't set up a pickup location yet. Without this, orders will be picked up from our default warehouse location.
                </p>
              </div>
            )}

            <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Location Name
              </Label>
              <Input
                id="name"
                placeholder="e.g., Main Warehouse, Shop Location"
                value={location.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Contact Phone *
              </Label>
              <Input
                id="phone"
                placeholder="+234 8XX XXX XXXX"
                value={location.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Contact Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="warehouse@yourbusiness.com"
              value={location.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="street" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Street Address *
            </Label>
            <Input
              id="street"
              placeholder="123 Main Street, Building Name"
              value={location.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                placeholder="e.g., Victoria Island"
                value={location.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Select
                value={location.state}
                onValueChange={(value) => handleInputChange('state', value)}
              >
                <SelectTrigger id="state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {nigerianStates.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Pickup Location
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Faramove Drop-off Hubs - Only shown when partner delivery is selected */}
      {deliveryPreference === 'partner' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Navigation className="h-5 w-5" />
              Fez Drop-off Hub Locations
            </CardTitle>
            <CardDescription>
              You can also drop off packages at any of these Fez hub locations instead of scheduling a pickup.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {DROPOFF_HUBS.map((hub, index) => (
                <div 
                  key={index} 
                  className="p-3 sm:p-4 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col gap-2">
                    <h4 className="font-semibold text-sm sm:text-base leading-tight">{hub.name}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>{hub.address}</span>
                    </p>
                    {hub.phone && (
                      <a 
                        href={`tel:${hub.phone.replace(/\s/g, '')}`} 
                        className="text-xs sm:text-sm text-primary hover:underline flex items-center gap-1.5 w-fit"
                      >
                        <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{hub.phone}</span>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              💡 Tip: Dropping off at a hub can be faster than waiting for pickup and may reduce delivery costs.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Self-Delivery Fee Settings */}
      {deliveryPreference === 'self' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Delivery Fee Settings
            </CardTitle>
            <CardDescription>
              Set your delivery charges for self-delivery orders
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Fee Type Selection */}
            <div className="space-y-3">
              <Label>Fee Type</Label>
              <RadioGroup
                value={selfDeliveryFeeType}
                onValueChange={(value) => setSelfDeliveryFeeType(value as 'fixed' | 'per_location')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="fixed" id="fixed" />
                  <Label htmlFor="fixed" className="cursor-pointer">Fixed Fee (Same for all locations)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="per_location" id="per_location" />
                  <Label htmlFor="per_location" className="cursor-pointer">Per Location (Different fees per state)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Fixed Fee Input */}
            {selfDeliveryFeeType === 'fixed' && (
              <div className="space-y-2">
                <Label htmlFor="fixed_fee">Delivery Fee (₦)</Label>
                <Input
                  id="fixed_fee"
                  type="number"
                  min="0"
                  placeholder="e.g., 1500"
                  value={selfDeliveryFixedFee || ''}
                  onChange={(e) => setSelfDeliveryFixedFee(Number(e.target.value) || 0)}
                  className="max-w-xs"
                />
                <p className="text-sm text-muted-foreground">This fee will be applied to all orders regardless of destination.</p>
              </div>
            )}

            {/* Per Location Fees */}
            {selfDeliveryFeeType === 'per_location' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Location-Based Fees</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLocationFee}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Location
                  </Button>
                </div>

                {selfDeliveryLocationFees.length === 0 ? (
                  <div className="text-center py-6 border border-dashed rounded-lg">
                    <p className="text-muted-foreground text-sm">No location fees added yet. Click "Add Location" to set fees for different states.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selfDeliveryLocationFees.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                        <Select
                          value={item.state}
                          onValueChange={(value) => updateLocationFee(index, 'state', value)}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {nigerianStates.map((state) => (
                              <SelectItem key={state} value={state} disabled={selfDeliveryLocationFees.some((f, i) => i !== index && f.state === state)}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">₦</span>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Fee"
                            value={item.fee || ''}
                            onChange={(e) => updateLocationFee(index, 'fee', Number(e.target.value) || 0)}
                            className="w-28"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLocationFee(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-muted-foreground">States not listed will use a default message asking the buyer to contact you for delivery arrangements.</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Delivery Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PickupLocationSettings;
